import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Button, CircularProgress, Snackbar, Alert as MuiAlert, Typography, Pagination } from '@mui/material';
import EventCard from '../components/EventsComponents/EventCard';
import EventsFiltersToolbar from '../components/EventsComponents/EventsFiltersToolbar';
import { theme } from '../theme/colors';
import AuthRequiredModal from '../components/AlertComponents/AuthRequiredModal';
import { fetchEventTypes } from '../utils/eventApi';
import { useEventsFilters } from '../hooks/useEventsFilters';
import { useEventsLocationSearch } from '../hooks/useEventsLocationSearch';
import { formatNearZipLocationLabel } from '../utils/eventProximity';
import { getUserProfile } from '../utils/discoverApi';
import { useUser } from '../context/UserContext';
import { MOBILIZE_EVENTS_ENABLED } from '../utils/featureFlags';
import '../styles/component_styles/Events.scss';

const EVENTS_PER_PAGE = 24;

export default function EventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizerId = searchParams.get('organizer') || '';
  const nearZipParam = searchParams.get('near_zip') || '';

  const { user, accountTypeName, userState, loadingUser, canCreateEvent } = useUser();
  const isLoggedIn = !!user;

  const showHostEventButton = Boolean(
    isLoggedIn && (canCreateEvent || accountTypeName)
  );

  const initialFilters = useMemo(() => {
    const nearZip = nearZipParam.trim().slice(0, 5);
    const hasNearZip = /^\d{5}$/.test(nearZip);
    const locationFromUrl = searchParams.get('location_search') || '';
    return {
      event_type: searchParams.get('event_type') || '',
      locationSearch: hasNearZip
        ? formatNearZipLocationLabel(nearZip, searchParams.get('state') || userState || '')
        : locationFromUrl,
      rsvp: searchParams.get('rsvp') || '',
      civix_only: searchParams.get('civix_only') || '',
      nearZip: hasNearZip ? nearZip : '',
    };
  }, [searchParams, nearZipParam, userState]);

  const {
    events, loading, error,
    selectedEventType, setSelectedEventType,
    locationSearchInput, setLocationSearchInput,
    submittedLocationSearch, setSubmittedLocationSearch,
    useCurrentLocation, setUseCurrentLocation,
    rsvpFilter, setRsvpFilter,
    civixOnly, setCivixOnly,
    loadEvents, getFilterQueryParams, loadingMore,
  } = useEventsFilters({
    organizerId,
    isLoggedIn,
    initialFilters,
    nearZip: initialFilters.nearZip,
  });

  const [eventTypes, setEventTypes] = useState([]);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [organizerDisplayName, setOrganizerDisplayName] = useState('');
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(events.length / EVENTS_PER_PAGE);
  const pageStart = (page - 1) * EVENTS_PER_PAGE;
  const pageEvents = useMemo(
    () => events.slice(pageStart, pageStart + EVENTS_PER_PAGE),
    [events, pageStart],
  );

  // A new result set means the old page number no longer refers to anything.
  useEffect(() => { setPage(1); }, [events]);

  const handlePageChange = useCallback((_e, nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const locationSearch = useEventsLocationSearch({
    isLoggedIn,
    userState,
    locationSearchInput,
    setLocationSearchInput,
    submittedLocationSearch,
    setSubmittedLocationSearch,
    useCurrentLocation,
    setUseCurrentLocation,
    loadEvents,
    onNotify: notify,
  });

  useEffect(() => {
    if (!organizerId) {
      setOrganizerDisplayName('');
      return;
    }
    let cancelled = false;
    getUserProfile(organizerId)
      .then((profile) => {
        if (cancelled || !profile) return;
        const name = profile.entity_name
          || (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : null)
          || profile.username
          || 'this organizer';
        setOrganizerDisplayName(name);
      })
      .catch(() => { if (!cancelled) setOrganizerDisplayName('this organizer'); });
    return () => { cancelled = true; };
  }, [organizerId]);

  useEffect(() => {
    if (loadingUser) return;
    if (!isLoggedIn && !sessionStorage.getItem('events_guest_prompt_shown')) {
      sessionStorage.setItem('events_guest_prompt_shown', '1');
      setShowGuestPrompt(true);
    }
  }, [loadingUser, isLoggedIn]);

  useEffect(() => {
    fetchEventTypes().then(setEventTypes).catch(() => {});
  }, []);

  const filterQs = getFilterQueryParams().toString();
  const listHref = filterQs ? `/events?${filterQs}` : '/events';
  const mapHref = filterQs ? `/events/map?${filterQs}` : '/events/map';

  const handleClearOrganizerFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('organizer');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClearAllFilters = useCallback(() => {
    setSelectedEventType('');
    setRsvpFilter('');
    setCivixOnly(false);
    locationSearch.handleClearSearch();
  }, [setSelectedEventType, setRsvpFilter, setCivixOnly, locationSearch]);

  const hasActiveFilters = Boolean(
    organizerId ||
    submittedLocationSearch?.trim() ||
    nearZipParam.trim() ||
    selectedEventType ||
    rsvpFilter ||
    (MOBILIZE_EVENTS_ENABLED && civixOnly)
  );

  const handleHostEvent = () => {
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    }
    if (!canCreateEvent && !accountTypeName) {
      notify('Complete account setup to host events.', 'warning');
      return;
    }
    navigate('/events/create');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 2 }}>
      <AuthRequiredModal
        open={showGuestPrompt}
        onClose={() => setShowGuestPrompt(false)}
        onLogin={() => navigate('/auth/login')}
        title="You're browsing public events"
        description="Sign in to RSVP, volunteer, host events, and track events you're interested in."
        confirmLabel="Sign in"
        dismissLabel="Keep browsing"
      />
      <Container maxWidth="lg" sx={{ px: 0 }}>
        {organizerId && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#ecfdf5', borderRadius: '12px', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, color: '#065f46', fontWeight: 600 }}>
              Events by {organizerDisplayName || '…'}
            </Typography>
            <Button onClick={handleClearOrganizerFilter} size="small" sx={{ textTransform: 'none', fontWeight: 700, color: theme.primary }}>
              Clear
            </Button>
          </Box>
        )}

        <EventsFiltersToolbar
          viewMode="list"
          listHref={listHref}
          mapHref={mapHref}
          eventTypes={eventTypes}
          selectedEventType={selectedEventType}
          onEventTypeChange={setSelectedEventType}
          isLoggedIn={isLoggedIn}
          rsvpFilter={rsvpFilter}
          onRsvpFilterChange={setRsvpFilter}
          civixOnly={civixOnly}
          onCivixOnlyChange={setCivixOnly}
          showHostEventButton={showHostEventButton}
          onHostEvent={handleHostEvent}
          locationSearchInput={locationSearchInput}
          setLocationSearchInput={setLocationSearchInput}
          submittedLocationSearch={submittedLocationSearch}
          setSubmittedLocationSearch={setSubmittedLocationSearch}
          setUseCurrentLocation={setUseCurrentLocation}
          onClearAllFilters={handleClearAllFilters}
          sx={{ mb: 4 }}
          {...locationSearch}
        />

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={40} sx={{ color: theme.primary }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#dc2626' }}>
            <p style={{ marginBottom: 16 }}>{error}</p>
            <Button onClick={loadEvents} variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primaryHover } }}>
              Retry
            </Button>
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>No events found</h3>
            {hasActiveFilters && (
              <p style={{ color: '#64748b', margin: 0 }}>Try different filters.</p>
            )}
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 600, mb: 1.5, px: 0.5 }}>
              {events.length === 1
                ? '1 event'
                : `Showing ${pageStart + 1}–${pageStart + pageEvents.length} of ${events.length} events`}
              {loadingMore && ' · Loading Mobilize events…'}
            </Typography>

            <div className="events-grid">
              {pageEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  siblingCount={1}
                  boundaryCount={1}
                  aria-label="Events pages"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontWeight: 600,
                      color: '#334155',
                      minWidth: 40,
                      height: 40,
                    },
                    '& .Mui-selected': {
                      bgcolor: `${theme.primary} !important`,
                      color: '#fff',
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}

        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MuiAlert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {toast.message}
          </MuiAlert>
        </Snackbar>
      </Container>
    </Box>
  );
}
